/**
 * Service API pour le module Surprise du Jour (Firebase Direct)
 * MIGRATION FIREBASE: Utilise Firebase directement au lieu de l'API Python
 */
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Helper pour sérialiser les timestamps
const serializeTimestamps = (data) => {
  const result = { ...data };
  for (const key of Object.keys(result)) {
    if (result[key] instanceof Timestamp) {
      result[key] = result[key].toDate().toISOString();
    }
  }
  return result;
};

// Helper pour convertir snapshot en array
const docsToArray = (snapshot) => {
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...serializeTimestamps(doc.data())
  }));
};

// ==================== STATS & DASHBOARD ====================

export const getStats = async () => {
  try {
    // Calculer les stats depuis les collections Firestore
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Charger les plays et rewards
    const [playsSnapshot, rewardsSnapshot] = await Promise.all([
      getDocs(collection(db, 'surprise_plays')),
      getDocs(collection(db, 'surprise_rewards'))
    ]);

    const plays = docsToArray(playsSnapshot).map(play => {
      if (typeof play.won === 'boolean' || !play.result || typeof play.result !== 'object') return play;
      const item = play.result;
      const lost = ['nothing', 'lose', 'lost'].includes(String(item.reward_type || item.type || '').toLowerCase()) || String(item.label || '').toLowerCase().includes('perdu');
      return { ...play, won: !lost };
    });
    const rewards = docsToArray(rewardsSnapshot);

    // Calculer les stats
    const spinsToday = plays.filter(p => {
      const playDate = p.created_at ? new Date(p.created_at) : null;
      return playDate && playDate >= today;
    }).length;

    const playsThisMonth = plays.filter(p => {
      const playDate = p.created_at ? new Date(p.created_at) : null;
      return playDate && playDate >= firstOfMonth;
    });

    const spinsMonth = playsThisMonth.length;

    // FIX Build 199: Calculer wins et losses du mois
    const winsMonth = playsThisMonth.filter(p => p.won === true).length;
    const lossesMonth = playsThisMonth.filter(p => p.won === false).length;
    // Calculer le taux de victoire
    const winRate = spinsMonth > 0 ? Math.round((winsMonth / spinsMonth) * 100) : 0;

    const activeRewards = rewards.filter(r => r.status === 'active').length;
    const usedRewards = rewards.filter(r => r.status === 'used').length;
    const expiredRewards = rewards.filter(r => r.status === 'expired').length;

    // Calculer le coût mensuel (valeur des récompenses utilisées ce mois)
    const monthlyCost = rewards
      .filter(r => {
        const usedDate = r.used_at ? new Date(r.used_at) : null;
        return r.status === 'used' && usedDate && usedDate >= firstOfMonth;
      })
      .reduce((sum, r) => sum + (r.reward_value || 0), 0);

    // FIX Build 199: Coût potentiel (toutes les récompenses actives + pending)
    const monthlyCostPotential = rewards
      .filter(r => r.status === 'active' || r.status === 'pending')
      .reduce((sum, r) => sum + (r.reward_value || 0), 0);

    // Taux de conversion (récompenses utilisées / total distribuées)
    const totalDistributed = usedRewards + activeRewards;
    const conversionRate = totalDistributed > 0 ? Math.round((usedRewards / totalDistributed) * 100) : 0;

    return {
      spins_today: spinsToday,
      spins_month: spinsMonth,
      // FIX Build 199: Stats gagnants/perdants
      wins_month: winsMonth,
      losses_month: lossesMonth,
      win_rate: winRate,
      active_rewards: activeRewards,
      used_rewards: usedRewards,
      expired_rewards: expiredRewards,
      monthly_cost: monthlyCost,
      monthly_cost_potential: monthlyCostPotential,
      conversion_rate: conversionRate
    };
  } catch (error) {
    console.error('Erreur getStats:', error);
    return {
      spins_today: 0,
      spins_month: 0,
      wins_month: 0,
      losses_month: 0,
      win_rate: 0,
      active_rewards: 0,
      used_rewards: 0,
      expired_rewards: 0,
      monthly_cost: 0,
      monthly_cost_potential: 0,
      conversion_rate: 0
    };
  }
};

// ==================== CONFIG / PROBABILITÉS (wheel_items) ====================

export const getConfigs = async () => {
  try {
    // Les configs sont stockées dans settings/surprise_du_jour.wheel_items
    const docRef = await getDoc(doc(db, 'settings', 'surprise_du_jour'));
    if (!docRef.exists()) return { configs: [] };

    const data = docRef.data();
    const wheelItems = data.wheel_items || [];

    // Transformer wheel_items en configs avec IDs
    const configs = wheelItems.map((item, index) => ({
      id: item.id || `item_${index}`,
      ...item
    }));

    return { configs };
  } catch (error) {
    console.error('Erreur getConfigs:', error);
    return { configs: [] };
  }
};

export const createConfig = async (configData) => {
  try {
    // Ajouter à wheel_items dans settings/surprise_du_jour
    const docRef = doc(db, 'settings', 'surprise_du_jour');
    const docSnap = await getDoc(docRef);

    const currentData = docSnap.exists() ? docSnap.data() : {};
    const wheelItems = currentData.wheel_items || [];

    const newItem = {
      id: `item_${Date.now()}`,
      ...configData,
      created_at: new Date().toISOString()
    };

    wheelItems.push(newItem);

    await setDoc(docRef, {
      ...currentData,
      wheel_items: wheelItems,
      updated_at: serverTimestamp()
    }, { merge: true });

    return { success: true, config: newItem };
  } catch (error) {
    console.error('Erreur createConfig:', error);
    throw error;
  }
};

export const updateConfig = async (configId, updateData) => {
  try {
    const docRef = doc(db, 'settings', 'surprise_du_jour');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) throw new Error('Settings not found');

    const currentData = docSnap.data();
    const wheelItems = currentData.wheel_items || [];

    const index = wheelItems.findIndex(item => item.id === configId);
    if (index === -1) throw new Error('Config not found');

    wheelItems[index] = {
      ...wheelItems[index],
      ...updateData,
      updated_at: new Date().toISOString()
    };

    await updateDoc(docRef, {
      wheel_items: wheelItems,
      updated_at: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Erreur updateConfig:', error);
    throw error;
  }
};

export const deleteConfig = async (configId) => {
  try {
    const docRef = doc(db, 'settings', 'surprise_du_jour');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) throw new Error('Settings not found');

    const currentData = docSnap.data();
    const wheelItems = (currentData.wheel_items || []).filter(item => item.id !== configId);

    await updateDoc(docRef, {
      wheel_items: wheelItems,
      updated_at: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Erreur deleteConfig:', error);
    throw error;
  }
};

// ==================== SETTINGS ====================

export const getSettings = async () => {
  try {
    const docRef = await getDoc(doc(db, 'settings', 'surprise_du_jour'));
    if (!docRef.exists()) {
      return { module_active: true, reward_expiry_days: 7 };
    }
    return serializeTimestamps(docRef.data());
  } catch (error) {
    console.error('Erreur getSettings:', error);
    return { module_active: true, reward_expiration_days: 7 };
  }
};

export const updateSettings = async (settings) => {
  try {
    await setDoc(doc(db, 'settings', 'surprise_du_jour'), {
      ...settings,
      updated_at: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Erreur updateSettings:', error);
    throw error;
  }
};

// ==================== RÉCOMPENSES ====================

export const getAllRewards = async (status = null) => {
  try {
    let q = collection(db, 'surprise_rewards');

    if (status) {
      q = query(collection(db, 'surprise_rewards'), where('status', '==', status));
    }

    const snapshot = await getDocs(q);
    return { rewards: docsToArray(snapshot) };
  } catch (error) {
    console.error('Erreur getAllRewards:', error);
    return { rewards: [] };
  }
};

// ==================== ANTI-CHEAT ====================

/**
 * Récupère les logs anti-triche pour détecter les comportements suspects
 * Analyse les plays pour trouver:
 * - Spins multiples le même jour par utilisateur
 * - Jackpots multiples en moins de 12h
 * - IPs utilisées par plusieurs utilisateurs différents
 */
export const getAntiCheatLogs = async () => {
  try {
    const playsSnapshot = await getDocs(collection(db, 'surprise_plays'));
    const plays = docsToArray(playsSnapshot);

    // 1. Détecter les spins multiples le même jour par utilisateur
    const playsByUserByDay = {};
    plays.forEach(play => {
      if (!play.user_id || !play.created_at) return;
      const day = new Date(play.created_at).toISOString().split('T')[0];
      const key = `${play.user_id}_${day}`;
      if (!playsByUserByDay[key]) {
        playsByUserByDay[key] = { user_id: play.user_id, user_email: play.user_email, day, plays: [] };
      }
      playsByUserByDay[key].plays.push(play);
    });

    const multipleSpins = Object.values(playsByUserByDay)
      .filter(group => group.plays.length > 1)
      .map(group => ({
        user_id: group.user_id,
        user_email: group.user_email || 'N/A',
        date: group.day,
        count: group.plays.length,
        plays: group.plays
      }))
      .sort((a, b) => b.count - a.count);

    // 2. Détecter les jackpots multiples (wins avec reward_type contenant "jackpot" ou "menu")
    const jackpotPlays = plays.filter(p =>
      p.won && (p.reward_type?.toLowerCase().includes('jackpot') ||
                p.reward_type?.toLowerCase().includes('menu') ||
                p.reward_type?.toLowerCase().includes('offert'))
    );

    const jackpotsByUser = {};
    jackpotPlays.forEach(play => {
      if (!play.user_id) return;
      if (!jackpotsByUser[play.user_id]) {
        jackpotsByUser[play.user_id] = { user_id: play.user_id, user_email: play.user_email, jackpots: [] };
      }
      jackpotsByUser[play.user_id].jackpots.push(play);
    });

    const multipleJackpots = Object.values(jackpotsByUser)
      .filter(group => group.jackpots.length > 1)
      .map(group => ({
        user_id: group.user_id,
        user_email: group.user_email || 'N/A',
        count: group.jackpots.length,
        jackpots: group.jackpots.map(j => ({
          date: j.created_at,
          reward_type: j.reward_type
        }))
      }))
      .sort((a, b) => b.count - a.count);

    // 3. Détecter les IPs suspectes (même IP, utilisateurs différents)
    const playsByIP = {};
    plays.forEach(play => {
      if (!play.ip_address) return;
      if (!playsByIP[play.ip_address]) {
        playsByIP[play.ip_address] = new Set();
      }
      if (play.user_id) {
        playsByIP[play.ip_address].add(play.user_id);
      }
    });

    const suspiciousIPs = Object.entries(playsByIP)
      .filter(([ip, users]) => users.size > 1)
      .map(([ip, users]) => ({
        ip_address: ip,
        user_count: users.size,
        user_ids: Array.from(users)
      }))
      .sort((a, b) => b.user_count - a.user_count);

    return {
      multiple_spins: multipleSpins,
      multiple_jackpots: multipleJackpots,
      suspicious_ips: suspiciousIPs,
      total_plays_analyzed: plays.length
    };
  } catch (error) {
    console.error('Erreur getAntiCheatLogs:', error);
    return {
      multiple_spins: [],
      multiple_jackpots: [],
      suspicious_ips: [],
      total_plays_analyzed: 0
    };
  }
};

// ==================== TEST MODE ====================

/**
 * Effectue un spin de test (simulation admin)
 * Simule le tirage pondéré sans enregistrer dans Firebase
 * Utile pour tester les probabilités configurées
 */
export const testSpin = async (forceType = null) => {
  try {
    // Récupérer la configuration de la roue
    const docRef = await getDoc(doc(db, 'settings', 'surprise_du_jour'));
    if (!docRef.exists()) {
      return { success: false, message: 'Aucune configuration trouvée' };
    }

    const data = docRef.data();
    const wheelItems = (data.wheel_items || []).filter(item => item.is_active !== false);

    if (wheelItems.length === 0) {
      return { success: false, message: 'Aucune récompense active configurée' };
    }

    // Si forceType est spécifié, sélectionner ce type
    if (forceType) {
      const forcedItem = wheelItems.find(item =>
        item.type === forceType || item.reward_type === forceType
      );
      if (forcedItem) {
        return {
          success: true,
          mode: 'forced',
          reward: forcedItem,
          message: `Simulation forcée: ${forcedItem.label || forcedItem.type}`
        };
      }
    }

    // Vérifier la probabilité de gain globale
    const winProbability = data.win_probability || 70;
    const randomWin = Math.random() * 100;

    if (randomWin > winProbability) {
      return {
        success: true,
        mode: 'simulation',
        won: false,
        reward: null,
        message: `Pas de gain (tirage: ${randomWin.toFixed(1)}% > seuil: ${winProbability}%)`,
        details: {
          random_value: randomWin.toFixed(2),
          win_threshold: winProbability
        }
      };
    }

    // Tirage pondéré parmi les récompenses
    const totalWeight = wheelItems.reduce((sum, item) => sum + (item.probability || item.weight || 1), 0);
    let random = Math.random() * totalWeight;
    let selectedReward = wheelItems[0];

    for (const item of wheelItems) {
      random -= (item.probability || item.weight || 1);
      if (random <= 0) {
        selectedReward = item;
        break;
      }
    }

    return {
      success: true,
      mode: 'simulation',
      won: true,
      reward: selectedReward,
      message: `Gain simulé: ${selectedReward.label || selectedReward.type}`,
      details: {
        total_weight: totalWeight,
        items_count: wheelItems.length,
        win_probability: winProbability,
        reward_probability: selectedReward.probability || selectedReward.weight || 1
      }
    };
  } catch (error) {
    console.error('Error in testSpin:', error);
    return { success: false, message: error.message };
  }
};

export default {
  getStats,
  getConfigs,
  createConfig,
  updateConfig,
  deleteConfig,
  getSettings,
  updateSettings,
  getAllRewards,
  getAntiCheatLogs,
  testSpin
};
